import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../redux/HooksRedux';
import {
  OptionI,
  ValidationObj,
  WithIncomeInterface
} from '../../../../types/interface';
import * as SubsidyWithIncomeAction from '../../../redux/SubsidyWithIncome/SubsidyWithIncomeAction';
import {
  capitalizeFirstLetter,
  checkAccess,
  convertToCurrency,
  convertToLabelValue,
  formatDateOnly,
  parseIntoNumber
} from 'utils/helpers';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import validationFunctions from 'utils/validationUtils';
import { SubsidyWithIncomeValidationSet } from 'utils/validations/ValidationsSet/SubsidyWithIncomeValidationSet';
import { Card } from 'react-bootstrap';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardLoader } from 'base/Loader/Loader';
import { Form } from 'react-bootstrap';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { t } from 'i18next';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import StandardInput from 'base/StandardInput/StandardInput';
import { UilAngleLeft, UilTrashAlt } from '@iconscout/react-unicons';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { StandardCurrency } from 'base/StandardCurrency/StandardCurrency';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import { EDUCATION, HEALTH, modules } from 'utils/enums/misc';
import {
  DYNAMIC_APPLICATION_EDUCATION_ID,
  DYNAMIC_APPLICATION_HEALTH_ID
} from 'utils/enums/dynamic_misc';

interface WithIncomeSubsidyCalculationPropsI {
  disable?: boolean;
  application_type?: string;
  household_income_per_person?: number;
  net_income?: number;
  refunded_amount?: number;
}

export const WithIncomeSubsidyCalculation = (
  props: WithIncomeSubsidyCalculationPropsI
) => {
  const dispatch = useAppDispatch();
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [rowIndex, setRowIndex] = useState<{
    index: number;
    id: number | null;
  }>();
  const [apiCallFlag, setApiCallFlag] = useState(false);
  const [applicationTypeOption, setApplicationTypeOption] =
    useState<OptionI[]>();
  const [applicationType, setApplicationType] = useState<number>(100104);
  const [totalSupport, setTotalSupport] = useState<number | null>(null);
  const currentDate = new Date();
  const withIncomeData: WithIncomeInterface = {
    with_income_configuration_id: null,
    net_income: null,
    refund_percentage: null,
    created_at: currentDate.toDateString(),
    updated_at: currentDate.toDateString(),
    maximum_support_provided_per_child: null,
    maximum_support_provided_per_user_per_year: null,
    configuration_threshold_invoice: null,
    support_provided: null
  };
  const [formData, setFormData] = useState<WithIncomeInterface[]>([
    withIncomeData
  ]);
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const exampleHouseholdIncome = 1800;
  const exampleNetInvoiceAmount = 1000;
  const [netInvoiceAmount, setNetInvoiceAmount] = useState(0);
  const [refundPercentage, setRefundPercentage] = useState(0);
  const [supportPerChild, setSupportPerChild] = useState<number | null>(0);
  const [supportPerYear, setSupportPerYear] = useState<number | null>(0);
  const [configurationThresholdInvoice, setConfigurationThresholdInvoice] =
    useState<number | null>(0);
  const [supportProvided, setSupportProvided] = useState<number | null>(0);

  const handleAddRow = () => {
    setFormData(prevFormData => [...prevFormData, { ...withIncomeData }]);
    setShowError(false);
  };

  const handleDeleteRow = async () => {
    if (rowIndex?.id) {
      try {
        const response = await dispatch(
          SubsidyWithIncomeAction.deleteWithIncomeConfiguration(rowIndex?.id)
        );

        if (response.status === 200) {
          setFormData(prevFormData =>
            prevFormData.filter(
              item => item.with_income_configuration_id !== rowIndex?.id
            )
          );
          setShowPopup(false);
          successNotification(response.data?.message);
        } else {
          errorNotification(response.data?.data?.message);
          setShowPopup(false);
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      setFormData(prevFormData =>
        prevFormData.filter((_, index) => index !== rowIndex?.index)
      );
      setShowPopup(false);
    }
  };

  const handleChange = (
    index: number,
    key: keyof WithIncomeInterface,
    value: WithIncomeInterface[keyof WithIncomeInterface]
  ) => {
    setFormData(prevFormData => {
      const updatedFormData = [...prevFormData];
      updatedFormData[index] = {
        ...updatedFormData[index],
        [key]: value ? parseFloat(value as any) : null
      };
      return updatedFormData;
    });
  };

  const getAllApplicationTypeRequest = async () => {
    try {
      const response = await dispatch(
        SubsidyWithIncomeAction.getAllApplicationTypeOption()
      );

      if (response.status === 200) {
        const convertValue = convertToLabelValue(
          response.data.data,
          'name',
          'application_request_id'
        );
        setApplicationTypeOption(convertValue);
      }
    } catch (error) {
      console.log(error);
    }
  };

  function getByValue(map: Map<any, any>, searchValue: any) {
    for (let [key, value] of map.entries()) {
      if (value === searchValue) return key;
    }
  }

  const getAllWithHouseholdCalculation = async (application_type: number) => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        SubsidyWithIncomeAction.getAllWithIncomeConfiguration(
          Number(application_type)
        )
      );
      if (response?.status === 200) {
        if (response.data.data.length == 0) {
          setApiCallFlag(false);
          setFormData([withIncomeData]);
        } else {
          setApiCallFlag(true);
          setFormData(response.data?.data);
          setTotalSupport(response.data?.data[0].maximum_support_provided);
          setSupportPerChild(
            response.data?.data[0].maximum_support_provided_per_child
          );
          setSupportPerYear(
            response.data?.data[0].maximum_support_provided_per_user_per_year
          );
          setConfigurationThresholdInvoice(
            response.data?.data[0].configuration_threshold_invoice
          );
          setSupportProvided(response.data?.data[0].support_provided);
        }
        setIsLoading(false);
      } else {
        errorNotification(response.data?.data?.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsLoading(true);
    e.preventDefault();
    const apiData = {
      configurations: formData.map(item => ({
        ...item,
        maximum_support_provided: totalSupport,
        maximum_support_provided_per_child: supportPerChild,
        maximum_support_provided_per_user_per_year: supportPerYear,
        configuration_threshold_invoice: configurationThresholdInvoice,
        support_provided: supportProvided
      }))
    };

    const duplicateValueMap = new Map();
    formData.forEach(item => {
      if (duplicateValueMap.has(item.net_income)) {
        duplicateValueMap.set(item.net_income, 2);
      } else {
        duplicateValueMap.set(item.net_income, 1);
      }
    });

    const hasDuplicateValue = getByValue(duplicateValueMap, 2);

    if (hasDuplicateValue) {
      setIsLoading(false);
      errorNotification(
        capitalizeFirstLetter(t('subsidy_calculation_with.message.value_13'))
      );
      return;
    }

    const isValid = apiData.configurations.every(item =>
      validationFunctions.checkFormValidity(
        item,
        SubsidyWithIncomeValidationSet
      )
    );

    if (!isValid) {
      setShowError(true);
      setIsLoading(false);
      return;
    }

    setShowError(false);

    try {
      const response = apiCallFlag
        ? await dispatch(
            SubsidyWithIncomeAction.updateWithIncomeConfiguration(
              Number(applicationType),
              apiData
            )
          )
        : await dispatch(
            SubsidyWithIncomeAction.createWithIncomeConfiguration(
              Number(applicationType),
              apiData
            )
          );

      if (
        (apiCallFlag && response?.status === 200) ||
        (!apiCallFlag && response?.status === 201)
      ) {
        const message = apiCallFlag
          ? response.data?.message
          : response?.data?.message;
        successNotification(message);
        getAllWithHouseholdCalculation(applicationType);
      } else {
        const errorMessage = apiCallFlag
          ? response.data?.data?.message
          : response?.data?.result?.message;
        errorNotification(errorMessage);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (props.application_type) {
      if (props.application_type === HEALTH) {
        getAllWithHouseholdCalculation(DYNAMIC_APPLICATION_HEALTH_ID as number);
      } else if (props.application_type === EDUCATION) {
        getAllWithHouseholdCalculation(
          DYNAMIC_APPLICATION_EDUCATION_ID as number
        );
      }
    } else {
      getAllWithHouseholdCalculation(applicationType);
      getAllApplicationTypeRequest();
    }
  }, [props.application_type]);

  useEffect(() => {
    const findMatchingIncome = () => {
      const sortedFormData = [...formData].sort(
        (a, b) => parseIntoNumber(a.net_income) - parseIntoNumber(b.net_income)
      );

      const incomeToCompare = props.disable
        ? parseIntoNumber(props.household_income_per_person)
        : exampleHouseholdIncome;

      const matchingEntry = sortedFormData.find(
        item => incomeToCompare <= parseIntoNumber(item.net_income)
      );

      if (matchingEntry) {
        setNetInvoiceAmount(parseIntoNumber(matchingEntry.net_income));
        setRefundPercentage(parseIntoNumber(matchingEntry.refund_percentage));
      } else {
        // Reset values if no matching entry found
        setNetInvoiceAmount(0);
        setRefundPercentage(0);
      }
    };

    findMatchingIncome();
  }, [props.disable, formData, props.household_income_per_person]);

  const handleChangeApplicationType = (value: number) => {
    setApplicationType(value);
    setIsLoading(true);
    getAllWithHouseholdCalculation(value);
  };

  const renderDeleteModal = () => {
    if (showPopup) {
      return (
        <DeleteModel
          onCancel={() => setShowPopup(false)}
          onConfirm={handleDeleteRow}
        />
      );
    }
  };

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <div
            className="m-3 p-3 border-radius-15  border-radius-10 has-box-shadow"
            id="event_module_id"
          >
            <ConditionalRender condition={!props.disable}>
              <div className="row pb-2">
                <div className="col-lg-12 d-flex flex-wrap justify-content-between align-items-center mx-1 padding-bottom-20 padding-top-6 gap-3 gap-lg-0">
                  <h4 className="col-lg-3 mb-0 fs-20 font-semi-bold">
                    {capitalizeFirstLetter(t('header.text.text_10'))}
                  </h4>

                  <div className="d-flex justify-content-end align-items-center">
                    <div
                      id="search_input"
                      className="position-relative h-40 w-300 mx-0 mx-lg-2"
                    >
                      <StandardSelect
                        placeholder={t(
                          'subsidy_calculation_with.placeholder.text_3'
                        )}
                        value={applicationType}
                        onChange={e => handleChangeApplicationType(e)}
                        className={'mb-0'}
                        options={applicationTypeOption as OptionI[]}
                        formClassName="w-330"
                        id=""
                        label=""
                        showError={false}
                        validations={[]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </ConditionalRender>
            <div className="row py-2 gap-3 gap-lg-0">
              <div className="col-lg-6 col-12">
                <StandardCurrency
                  type="number"
                  value={totalSupport as number}
                  placeholder={capitalizeFirstLetter(
                    t('subsidy_calculation_with.general.text_4')
                  )}
                  onChange={e => {
                    if (e) {
                      setTotalSupport(parseFloat(e?.toString()));
                    } else {
                      setTotalSupport(null);
                    }
                  }}
                  showError={showError}
                  className={''}
                  label={capitalizeFirstLetter(
                    t('subsidy_calculation_with.general.text_4')
                  )}
                  validations={
                    SubsidyWithIncomeValidationSet.support_provided as ValidationObj[]
                  }
                  disabled={props.disable}
                />
              </div>

              <ConditionalRender
                condition={applicationType !== DYNAMIC_APPLICATION_HEALTH_ID}
              >
                <div className="col-lg-6 col-12">
                  <StandardCurrency
                    value={supportPerChild}
                    placeholder={t('general.text_13')}
                    onChange={e => {
                      if (e) {
                        setSupportPerChild(parseFloat(e?.toString()));
                      } else {
                        setSupportPerChild(null);
                      }
                    }}
                    showError={false}
                    className={''}
                    label={capitalizeFirstLetter(t('general.text_13'))}
                    validations={[]}
                    disabled={props.disable}
                  />
                </div>
              </ConditionalRender>
              <div className="col-lg-6 col-12">
                <StandardCurrency
                  value={supportPerYear}
                  placeholder={t('general.text_14')}
                  onChange={e => {
                    if (e) {
                      setSupportPerYear(parseFloat(e?.toString()));
                    } else {
                      setSupportPerYear(null);
                    }
                  }}
                  showError={false}
                  className={''}
                  label={capitalizeFirstLetter(t('general.text_14'))}
                  validations={[]}
                  disabled={props.disable}
                />
              </div>
            </div>

            <div className="row py-2 gap-3 gap-lg-0">
              <div className="col-lg-6">
                <StandardCurrency
                  value={configurationThresholdInvoice}
                  placeholder={t(
                    'subsidy_calculation_without.placeholder.text_1'
                  )}
                  label={t(
                    'subsidy_calculation_without.placeholder.text_1'
                  )}
                  onChange={e => {
                    setConfigurationThresholdInvoice(e);
                  }}
                  showError={showError}
                  className={''}
                  validations={
                    SubsidyWithIncomeValidationSet.configuration_threshold_invoice
                  }
                  disabled={props.disable}
                />
              </div>
              <div className="col-lg-6">
                <StandardCurrency
                  value={supportProvided}
                  placeholder={t(
                    'subsidy_calculation_without.placeholder.text_2'
                  )}
                  label={t(
                    'subsidy_calculation_without.placeholder.text_2'
                  )}
                  onChange={e => {
                    setSupportProvided(e);
                  }}
                  showError={showError}
                  className={''}
                  validations={SubsidyWithIncomeValidationSet.support_provided}
                  disabled={props.disable}
                />
              </div>
            </div>
            <div className="row my-2 gap-3 gap-lg-0">
              <div className="col-lg-2"></div>
              <div className="col-lg-4 ">
                <h5 className="mb-0  font-semi-bold  ps-0 ms-0 ps-lg-2 ms-lg-1">
                  {t('subsidy_calculation_with.message.value_5')} (&euro;)
                </h5>
              </div>
              <div className="col-1"></div>
              <div className="col-lg-4 d-flex justify-content-start">
                <h5 className="mb-0  font-semi-bold ">
                  {t('subsidy_calculation_with.placeholder.text_2')} (%)
                </h5>
              </div>
            </div>
            {formData.map((item, index) => {
              return (
                <div
                  key={`with-income-subcidy-calculation-${index}`}
                  className="border rounded p-3 my-2"
                >
                  <div className="row gap-3 gap-lg-0">
                    <div className="col-lg-2 d-flex justify-content-center  ">
                      <h5 className="mb-0  font-semi-bold ">
                        {t('application.general.text_15')}
                      </h5>
                      <span className="h-40 fw-semibold fs-6">&#x2264;</span>
                    </div>

                    <div className="col-lg-4">
                      <StandardCurrency
                        type="number"
                        value={item.net_income as number}
                        placeholder={t(
                          'household_income_calculator.calculator.result.field_1'
                        )}
                        onChange={e =>
                          handleChange(index, 'net_income', Number(e))
                        }
                        showError={showError}
                        className={''}
                        disabled={props.disable}
                        validations={SubsidyWithIncomeValidationSet.net_income}
                        key={`with-income-net-invoice-${index}`}
                      />
                    </div>

                    <div className="col-lg-1">
                      <h5 className="h-40 mb-0 font-semi-bold d-flex justify-content-center align-items-center">
                        {t('subsidy_calculation_with.general.text_3')}
                      </h5>
                    </div>

                    <div className="col-lg-4">
                      <StandardInput
                        type="text"
                        value={item?.refund_percentage as number}
                        placeholder={t(
                          'subsidy_calculation_with.placeholder.text_2'
                        )}
                        onChange={e =>
                          handleChange(
                            index,
                            'refund_percentage',
                            parseIntoNumber(e)
                          )
                        }
                        showError={showError}
                        className={''}
                        validations={
                          SubsidyWithIncomeValidationSet.refund_percentage
                        }
                        disabled={props.disable}
                        label=""
                        key={`with-income-refundable-percentage-${index}`}
                      />
                    </div>

                    <div className="col-lg-1  h-40 d-flex justify-content-end justify-content-lg-start">
                      <ConditionalRender
                        condition={
                          checkAccess(
                            ['configuration.delete'],
                            userPermission
                          ) && !props.disable
                        }
                      >
                        <UilTrashAlt
                          className={`cursor-pointer  ${
                            formData.length < 2 &&
                            item.with_income_configuration_id
                              ? 'd-none'
                              : 'd-inline'
                          }`}
                          onClick={() => {
                            setShowPopup(true);
                            setRowIndex({
                              id: item.with_income_configuration_id,
                              index
                            });
                          }}
                        />
                      </ConditionalRender>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-lg-2"></div>

                    <div className="col-lg-4 fs-16 font-semi-bold d-flex align-items-center">
                      <span>
                        {t('subsidy_calculation_without.log.log_1')} : {'  '}
                      </span>
                      <span className="ms-2">
                        {formatDateOnly(item.created_at as string)}
                      </span>
                    </div>
                    <div className="col-lg-1"></div>
                    <div className="col-lg-4 fs-16 font-semi-bold d-flex align-items-center">
                      <span>
                        {t('subsidy_calculation_without.log.log_2')} : {'  '}
                      </span>
                      <span className=" ms-2">
                        {formatDateOnly(item.updated_at as string)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="row align-items-center justify-content-end `">
              <ConditionalRender
                condition={
                  checkAccess([modules.CONFIGURATION.create], userPermission) &&
                  !props.disable
                }
              >
                <div className="w-auto pe-4">
                  <StandardButton
                    onClick={handleAddRow}
                    buttonText={t('subsidy_calculation_with.buttons.text_2')}
                    className={`add-btn-style px-4  `}
                  />
                </div>
              </ConditionalRender>
            </div>
            <div className="row refund-eligibili border-top mt-1 pt-2">
              <div className="col-lg-12 mx-1">
                <h4 className=" py-1 font-semi-bold">
                  {capitalizeFirstLetter(
                    t('subsidy_calculation_with.message.value_1')
                  )}
                </h4>
                <div className="ms-3">
                  <p className="mb-1">
                    {t('subsidy_calculation_with.message.value_2')}{' '}
                    <strong>{t('application.general.text_15')}</strong>{' '}
                    {t('subsidy_calculation_with.message.value_4')}{' '}
                    <strong>
                      {t('subsidy_calculation_with.message.value_5')}
                    </strong>
                    {t('subsidy_calculation_with.message.value_6')}
                  </p>
                  <ul>
                    <li>
                      <ConditionalRender
                        condition={!props.disable}
                        fallBack={t(
                          'subsidy_calculation_with.income_message_1',
                          {
                            x: convertToCurrency(
                              props.household_income_per_person as number
                            ),
                            y: convertToCurrency(props.net_income as number)
                          }
                        )}
                      >
                        {t('subsidy_calculation_with.income_message_1', {
                          x: convertToCurrency(exampleHouseholdIncome),
                          y: convertToCurrency(exampleNetInvoiceAmount)
                        })}
                      </ConditionalRender>
                    </li>
                    <li>
                      {t('subsidy_calculation_with.income_message_2', {
                        Z: convertToCurrency(netInvoiceAmount),
                        P: refundPercentage
                      })}
                    </li>
                    <li>
                      <ConditionalRender
                        condition={!props.disable}
                        fallBack={t(
                          'subsidy_calculation_with.income_message_3',
                          {
                            R: convertToCurrency(
                              props.refunded_amount as number
                            )
                          }
                        )}
                      >
                        {t('subsidy_calculation_with.income_message_3', {
                          R: (netInvoiceAmount * refundPercentage) / 100
                        })}
                      </ConditionalRender>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <ConditionalRender
              condition={
                checkAccess(
                  [modules.CONFIGURATION.edit, modules.CONFIGURATION.create],
                  userPermission
                ) && !props.disable
              }
            >
              <div className="row justify-content-center">
                <div className="col-lg-2 col-auto">
                  <div className="table-button-style me-lg-4 me-md-1">
                    <StandardButton
                      onClick={() => {}}
                      type={'submit'}
                      buttonText={
                        !apiCallFlag
                          ? t('general.common_text.text_2')
                          : t('general.common_text.text_18')
                      }
                      className={'add-btn-style px-4 '}
                    />
                  </div>
                </div>
              </div>
            </ConditionalRender>
          </div>
        </Form>
      </ConditionalRender>
      {renderDeleteModal()}
    </Card>
  );
};
